import React, { useState, useEffect, useRef } from 'react';
import { Item, getItemPrice } from '../../types';
import StockIndicator from '../StockIndicator/StockIndicator';
import './GlobalSearchBar.scss';
import { HiOutlineBookOpen } from 'react-icons/hi2';

interface GlobalSearchBarProps {
  items: Item[];
  allMenusItems: Array<Item & { menu_name: string; menu_id: number }>;
  menus: any[];
  selectedMenuIndex: number;
  onMenuChange: (index: number) => void;
  onItemSelect: (item: Item) => void;
  pedidoType: 'Delivery' | 'Caja' | 'Restaurant' | 'Autoservicio';
  getItemStockInfo?: (itemId: number) => {
    hasShortage: boolean;
    hasWarning: boolean;
    shortageInfo?: any;
    warningInfo?: any;
  };
  autoFocus?: boolean;
}

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  items,
  allMenusItems,
  menus,
  selectedMenuIndex,
  onMenuChange,
  onItemSelect,
  pedidoType,
  getItemStockInfo,
  autoFocus = false,
}) => {
  const [searchText, setSearchText] = useState<string>('');
  const [filteredItems, setFilteredItems] = useState<Array<Item & { menu_name?: string; menu_id?: number }>>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Normalize text by removing accents/diacritics for accent-insensitive search
  const normalizeText = (text: string): string => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredItems([]);
      setShowSuggestions(false);
      return;
    }
    
    const normalizedSearch = normalizeText(searchText);
    
    // Defensive filtering with validation - accent-insensitive search
    const filtered = allMenusItems.filter((it) => {
      if (!it || !it.item_name) return false;
      const normalizedItemName = normalizeText(it.item_name);
      return normalizedItemName.includes(normalizedSearch);
    });
    
    setFilteredItems(filtered);
    setShowSuggestions(filtered.length > 0);
  }, [searchText, allMenusItems]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };
  const handleSuggestionClick = (item: Item) => {
    onItemSelect(item);
    setSearchText('');
    setFilteredItems([]);
    setShowSuggestions(false);
    
    // Re-focus the search input after selection so user can continue typing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };
  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 100);
  };

  return (
    <div className="global-item-search-bar-container">
      <div className="global-search-bar-inner" ref={wrapperRef}>
        <div className="search-input-container">
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="Buscar..."
            className="item-search-input"
          />
          {showSuggestions && (
            <ul className="suggestions-list">
              {filteredItems.map((item, index) => {
                // Validate item structure
                if (!item || !item.item_id) return null;
                
                const currentMenu = menus[selectedMenuIndex];
                const isFromCurrentMenu = currentMenu && item.menu_id === currentMenu.menu_id;
                const correctPrice = getItemPrice(item, pedidoType);
                const stockInfo = getItemStockInfo ? getItemStockInfo(item.item_id) : { hasShortage: false, hasWarning: false };
                
                // Determine CSS class for stock status
                const stockClass = stockInfo.hasShortage ? 'stock-shortage' : stockInfo.hasWarning ? 'stock-warning' : '';
                
                return (
                  <li
                    key={`${item.item_id}-${index}`}
                    className={`suggestion-item ${!isFromCurrentMenu ? 'suggestion-item--different-menu' : ''} ${stockClass}`}
                    onMouseDown={() => handleSuggestionClick(item)}
                  >
                    <div className="suggestion-item__content">
                      <span className="suggestion-item__name">
                        {item.item_name || 'Sin nombre'}
                        <StockIndicator
                          hasShortage={stockInfo.hasShortage}
                          hasWarning={stockInfo.hasWarning}
                          shortageInfo={stockInfo.shortageInfo}
                          warningInfo={stockInfo.warningInfo}
                          size="small"
                        />
                      </span>
                      <span className="suggestion-item__price">
                        ${correctPrice.toFixed(2)}
                      </span>
                    </div>
                    {item.menu_name && !isFromCurrentMenu && (
                      <span className="suggestion-item__menu-badge">{item.menu_name}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {menus.length > 1 && (
          <div className="menu-dropdown-container">
            <button
              className="menu-toggle-button"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <HiOutlineBookOpen/>
            </button>
            {menuOpen && (
              <ul className="menu-list">
                {menus.map((menu, idx) => (
                  <li
                    key={menu.menu_id}
                    className={`menu-list-item ${
                      idx === selectedMenuIndex ? 'active' : ''
                    }`}
                    onClick={() => {
                      onMenuChange(idx);
                      setMenuOpen(false);
                    }}
                  >
                    {menu.menu_name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchBar;
